import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, Check, FileText, Phone, CalendarClock, MapPin, Car } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const money = (n) => (n === undefined || n === null || n === '') ? '—' : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function QuotePublic() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true });
  const [accepting, setAccepting] = useState(false);

  const load = useCallback(() => {
    setState({ loading: true });
    fetch(`${API}/api/public/quotes/${token}/info`)
      .then((r) => { if (!r.ok) throw new Error('not found'); return r.json(); })
      .then((d) => setState({ loading: false, d }))
      .catch(() => setState({ loading: false, error: true }));
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const accept = async () => {
    if (accepting) return;
    setAccepting(true);
    try {
      const r = await fetch(`${API}/api/public/quotes/${token}/accept`, { method: 'POST' });
      const j = await r.json().catch(() => ({}));
      if (j.success) { setState((s) => ({ ...s, d: { ...s.d, accepted: true, converted: j.already_converted || s.d.converted } })); }
    } finally { setAccepting(false); }
  };

  const wrap = (children) => (
    <div style={{ minHeight: '100vh', background: '#0e0e10', color: '#f5f5f5', fontFamily: 'Inter, system-ui, sans-serif' }} data-testid="quote-public">
      <div style={{ maxWidth: 560, margin: '0 auto', padding: '32px 20px 64px' }}>{children}</div>
    </div>
  );
  const Brand = () => (
    <div style={{ marginBottom: 24 }}>
      <p style={{ color: '#C1272D', fontWeight: 800, letterSpacing: '0.28em', fontSize: 11, margin: 0 }}>YOUR AUTO CONCIERGE</p>
      <p style={{ color: '#9a9a9a', fontSize: 12, marginTop: 4 }}>Luxury Chauffeur &amp; Transportation · (678) 846-3009</p>
    </div>
  );

  if (state.loading) return wrap(<div style={{ display: 'flex', gap: 8, alignItems: 'center', color: '#9a9a9a', paddingTop: 40 }}><Loader2 className="animate-spin" style={{ height: 18, width: 18 }} /> Loading your quote…</div>);
  if (state.error) return wrap(<><Brand /><div style={{ background: '#1a1a1d', borderRadius: 16, padding: 24, border: '1px solid #2a2a2e' }}><h1 style={{ fontSize: 20, margin: 0 }}>Quote unavailable</h1><p style={{ color: '#9a9a9a', marginTop: 8, fontSize: 14 }}>This quote link is invalid or has expired. Please call (678) 846-3009.</p></div></>);

  const q = state.d;
  const done = q.accepted || q.converted;
  const Row = ({ icon: Icon, label, value }) => value ? (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 0', borderBottom: '1px solid #232327' }}>
      <Icon style={{ height: 16, width: 16, color: '#C1272D', marginTop: 2, flexShrink: 0 }} />
      <div><p style={{ margin: 0, fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#8a8a8a' }}>{label}</p>
        <p style={{ margin: '2px 0 0', fontSize: 14 }}>{value}</p></div>
    </div>
  ) : null;

  return wrap(
    <>
      <Brand />
      <div style={{ background: '#151517', borderRadius: 18, padding: 24, border: '1px solid #2a2a2e' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
          <div>
            <p style={{ margin: 0, fontSize: 10, letterSpacing: '0.2em', color: '#8a8a8a', textTransform: 'uppercase' }}>Quote · {q.quote_id}</p>
            <h1 style={{ margin: '4px 0 0', fontSize: 26 }}>{q.client_name || 'Your quote'}</h1>
          </div>
          <div style={{ textAlign: 'right' }}>
            <p style={{ margin: 0, fontSize: 10, color: '#8a8a8a', textTransform: 'uppercase', letterSpacing: '0.12em' }}>Total</p>
            <p style={{ margin: '2px 0 0', fontSize: 26, fontWeight: 800, color: '#C1272D' }} data-testid="qp-amount">{money(q.amount)}</p>
          </div>
        </div>

        <div style={{ marginTop: 16 }}>
          <Row icon={FileText} label="Service" value={[q.service_type, q.vehicle_name].filter(Boolean).join(' · ')} />
          <Row icon={CalendarClock} label="Date" value={q.event_date} />
          <Row icon={MapPin} label="Route" value={[q.pickup_location, q.dropoff_location].filter(Boolean).join(' → ')} />
          <Row icon={Car} label="Trip" value={[q.passengers ? `${q.passengers} passengers` : null, q.hours ? `${q.hours} hrs` : null].filter(Boolean).join(' · ')} />
        </div>

        <div style={{ marginTop: 16, background: '#0e0e10', borderRadius: 12, padding: 16 }}>
          {q.base_total != null && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#c8c8c8', padding: '3px 0' }}><span>Base</span><span>{money(q.base_total)}</span></div>}
          {q.gratuity != null && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#c8c8c8', padding: '3px 0' }}><span>Gratuity</span><span>{money(q.gratuity)}</span></div>}
          {q.sales_tax != null && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#c8c8c8', padding: '3px 0' }}><span>Sales tax</span><span>{money(q.sales_tax)}</span></div>}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, fontWeight: 800, padding: '8px 0 0', marginTop: 6, borderTop: '1px solid #232327' }}><span>Total</span><span style={{ color: '#C1272D' }}>{money(q.amount)}</span></div>
        </div>

        {q.notes && <p style={{ marginTop: 14, fontSize: 13, color: '#9a9a9a' }}>{q.notes}</p>}

        <div style={{ marginTop: 22, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {done ? (
            <div style={{ background: 'rgba(95,168,113,.14)', border: '1px solid rgba(95,168,113,.4)', borderRadius: 12, padding: 16, textAlign: 'center' }} data-testid="qp-accepted">
              <Check style={{ height: 28, width: 28, color: '#5fa871', margin: '0 auto' }} />
              <p style={{ margin: '8px 0 0', fontSize: 16, fontWeight: 700 }}>{q.converted ? 'Your reservation is confirmed' : 'Quote accepted — thank you!'}</p>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: '#9a9a9a' }}>Our team will reach out shortly to finalize the details. Questions? Call (678) 846-3009.</p>
            </div>
          ) : (
            <button onClick={accept} disabled={accepting} data-testid="qp-accept-btn"
              style={{ background: '#C1272D', color: '#fff', border: 'none', borderRadius: 12, padding: '15px 18px', fontSize: 16, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              {accepting ? <Loader2 className="animate-spin" style={{ height: 18, width: 18 }} /> : <Check style={{ height: 18, width: 18 }} />} Accept This Quote
            </button>
          )}
          <a href={`${API}/api/public/quotes/${token}`} target="_blank" rel="noreferrer" data-testid="qp-view-pdf"
            style={{ textAlign: 'center', color: '#c8c8c8', border: '1px solid #2a2a2e', borderRadius: 12, padding: '13px 18px', fontSize: 14, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <FileText style={{ height: 16, width: 16 }} /> View / Download PDF
          </a>
          <a href="tel:+16788463009" style={{ textAlign: 'center', color: '#8a8a8a', fontSize: 13, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginTop: 4 }}>
            <Phone style={{ height: 14, width: 14 }} /> (678) 846-3009
          </a>
        </div>
      </div>
      <p style={{ textAlign: 'center', color: '#6a6a6a', fontSize: 11, marginTop: 20 }}>Quoted pricing is valid for 7 days and subject to availability.</p>
    </>
  );
}
